import React, { useState, useEffect } from "react"
import Modal from "aldoo-ra/Modal"
import Button from "aldoo-ra/Button"
import { showNotification } from "aldoo-ra/Notification"
import Typography from "aldoo-ra/Typography"

// Import API functions
import {
  ScanForRebuildKeys,
  RebuildLocalizationKeys,
  StopTaskProgress,
} from "./api"

// Import failed keys modal
import FailedKeysModal from "./failed-rebuild-keys-dialog"

const LocalizationRebuildDialog = ({ isOpen, onClose, controller }) => {
  const [scanState, setScanState] = useState("idle") // idle, scanning, scanned, rebuilding, completed, error
  const [scanResults, setScanResults] = useState(null)
  const [rebuildTaskID, setRebuildTaskID] = useState(null)
  const [progress, setProgress] = useState(0)
  const [statusMessage, setStatusMessage] = useState("")
  const [current, setCurrent] = useState(0)
  const [total, setTotal] = useState(0)
  const [error, setError] = useState(null)
  const [result, setResult] = useState(null)

  // New state for failed keys
  const [failedKeys, setFailedKeys] = useState([])
  const [isFailedKeysModalOpen, setIsFailedKeysModalOpen] = useState(false)

  // Reset state when dialog closes
  useEffect(() => {
    if (isOpen) {
      startScan()
    } else {
      // Reset state when dialog closes
      setScanState("idle")
      setScanResults(null)
      setRebuildTaskID(null)
      setProgress(0)
      setStatusMessage("")
      setCurrent(0)
      setTotal(0)
      setError(null)
      setResult(null)
      setFailedKeys([])
      setIsFailedKeysModalOpen(false)
    }
  }, [isOpen])

  // Extract current and total from status message like "Processing AquaSiteCardGroup (581/1008)"
  const extractProgressInfo = (statusMsg) => {
    if (!statusMsg) return null

    const matches = statusMsg.match(/\((\d+)\/(\d+)\)/)
    if (matches && matches.length === 3) {
      return {
        current: parseInt(matches[1], 10),
        total: parseInt(matches[2], 10),
      }
    }
    return null
  }

  // Format a comprehensive progress message
  const getProgressMessage = () => {
    let message = statusMessage

    // If we have current/total but they're not part of the status message, add them
    if (
      current > 0 &&
      total > 0 &&
      !message.includes(`(${current}/${total})`)
    ) {
      message += ` (${current}/${total})`
    }

    return message
  }

  // Handle progress updates
  const handleProgressUpdate = (response) => {
    // Update progress percentage
    if (response && typeof response.progress === "number") {
      setProgress(response.progress)
    }

    // Update status message from response name
    if (response && response.name) {
      setStatusMessage(response.name)

      // Try to extract current/total from the status message
      const progressInfo = extractProgressInfo(response.name)
      if (progressInfo) {
        setCurrent(progressInfo.current)
        setTotal(progressInfo.total)
      }
    }

    // Store result when available
    if (response && response.result) {
      setResult(response.result)

      // Check for failed keys
      if (
        response.result.failedKeys &&
        Array.isArray(response.result.failedKeys)
      ) {
        setFailedKeys(response.result.failedKeys)
      } else if (response.result.failedKeysCount > 0) {
        // We know there are failed keys but don't have the details
      }
    }

    // Handle task completion
    if (response && response.status === "completed") {
      // Ensure progress shows 100% at completion
      setProgress(100)
      setScanState("completed")

      // Show success notification
      showNotification({
        message: "Localization rebuild completed successfully",
        className: "bg-admin_primary text-white",
      })

      // Refresh statistics
      refreshStatistics()
    } else if (
      response &&
      (response.status === "failed" || response.status === "error")
    ) {
      setError(response.error || "Rebuild process failed")
      setScanState("error")
    } else if (response && response.taskID) {
      // Store task ID if provided
      setRebuildTaskID(response.taskID)
    }
  }

  // Start scanning for new keys
  const startScan = async () => {
    setScanState("scanning")
    setError(null)
    setStatusMessage("Scanning for new localization keys...")

    try {
      const response = await ScanForRebuildKeys()

      if (response.result) {
        setScanResults(response)
        setScanState("scanned")
      } else {
        setError(response.error || "Failed to scan for new keys")
        setScanState("error")
      }
    } catch (err) {
      setError("An error occurred while scanning for new keys")
      setScanState("error")
    }
  }

  // Start the rebuild process
  const startRebuild = async () => {
    setScanState("rebuilding")
    setError(null)
    setProgress(0)
    setCurrent(0)
    setTotal(0)
    setStatusMessage("Starting rebuild process...")
    setFailedKeys([])

    try {
      // Call RebuildLocalizationKeys with the onResponse callback and report=true
      const response = await RebuildLocalizationKeys(handleProgressUpdate, true)

      // Store task ID if available
      if (response.taskID) {
        setRebuildTaskID(response.taskID)
      }

      // Handle error from initial response
      if (!response.result) {
        setError(response.error || "Failed to start rebuild process")
        setScanState("error")
      }
    } catch (err) {
      setError("An error occurred while starting the rebuild process")
      setScanState("error")
    }
  }

  // Helper function to refresh statistics
  const refreshStatistics = () => {
    if (controller) {
      // The controller exposes refreshStatistics.current which is the recommended way
      if (
        controller.refreshStatistics &&
        typeof controller.refreshStatistics.current === "function"
      ) {
        controller.refreshStatistics.current()
      }
      // The controller may have a getLanguageStatistics method that should be called
      else if (typeof controller.getLanguageStatistics === "function") {
        controller.getLanguageStatistics()
      }
      // If the controller has the onRefreshStatistics method, it expects to be passed a callback
      // We don't have a callback, but calling it might trigger internal refresh logic
      else if (typeof controller.onRefreshStatistics === "function") {
        // Passing a no-op function as a placeholder
        controller.onRefreshStatistics(() => {})
      }
    }
  }

  // Stop the rebuild process
  const stopRebuild = async () => {
    if (!rebuildTaskID) return

    try {
      let response

      if (controller && controller.StopTaskProgress) {
        response = await controller.StopTaskProgress(rebuildTaskID)
      } else {
        response = await StopTaskProgress(rebuildTaskID)
      }

      if (response.result) {
        showNotification({
          message: "Rebuild process stopped",
          className: "bg-admin_primary text-white",
        })

        setRebuildTaskID(null)
        setScanState("scanned")
      } else {
        setError(response.error || "Failed to stop rebuild process")
      }
    } catch (err) {
      setError("An error occurred while stopping the rebuild")
    }
  }

  // Open failed keys modal
  const openFailedKeysModal = () => {
    setIsFailedKeysModalOpen(true)
  }

  // Close failed keys modal
  const closeFailedKeysModal = () => {
    setIsFailedKeysModalOpen(false)
  }

  // Render the dialog content based on the current state
  const renderDialogContent = () => {
    switch (scanState) {
      case "idle":
      case "scanning":
        return (
          <div className="flex flex-col items-center justify-center p-8">
            <Typography variant="admin_body" className="mb-4 mt-0">
              Scanning for new localization keys...
            </Typography>
            <div className="w-16 h-16 border-4 border-admin_primary border-t-transparent rounded-full animate-spin"></div>
          </div>
        )

      case "scanned":
        return (
          <div className="flex flex-col p-8">
            {scanResults && scanResults.newKeysCount > 0 ? (
              <>
                <Typography
                  variant="admin_body"
                  className="mb-4 mt-0 text-center"
                >
                  Found {scanResults.newKeysCount} new keys
                </Typography>
                <Typography variant="admin_body" className="mb-6 text-center">
                  Would you like to rebuild the localization database with these
                  keys?
                </Typography>
                <div className="flex justify-center">
                  <Button
                    variant="admin_primary"
                    className="w-full sm:w-32"
                    onClick={startRebuild}
                  >
                    Rebuild
                  </Button>
                </div>
              </>
            ) : (
              <Typography
                variant="admin_body"
                className="text-center my-0 mb-4"
              >
                No new keys found in the system
              </Typography>
            )}
          </div>
        )

      case "rebuilding":
        return (
          <div className="flex flex-col p-8">
            <Typography variant="admin_body" className="text-center mb-4 mt-0">
              Rebuilding localization database
            </Typography>

            {/* Single consolidated progress message */}
            <Typography variant="admin_body" className="text-center mb-4">
              {getProgressMessage()}
            </Typography>

            {/* Progress bar */}
            <div className="my-5">
              <div className="w-full bg-admin_neutral_1 rounded h-4 relative">
                <div
                  className="bg-admin_primary h-full rounded transition-all duration-300 ease-in-out"
                  style={{
                    width: `${progress}%`,
                  }}
                ></div>
                <span className="absolute right-0 top-0 text-xs text-admin_text pr-2">
                  {progress}%
                </span>
              </div>
            </div>

            <div className="flex justify-center mt-4">
              <Button
                variant="admin_secondary"
                className="w-full sm:w-32"
                onClick={stopRebuild}
              >
                Stop
              </Button>
            </div>
          </div>
        )

      case "completed":
        const hasFailedKeys =
          result?.failedKeysCount > 0 && failedKeys.length > 0

        return (
          <div className="flex flex-col p-8">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-admin_success/10 rounded-full flex items-center justify-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-10 w-10 text-admin_success"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
            </div>
            <Typography variant="admin_body" className="text-center mt-0 mb-4">
              Rebuild Completed Successfully
            </Typography>

            {/* Success message - fixed to remove unintended zeros */}
            <Typography variant="admin_body" className="mt-0 mb-6 text-center">
              {result ? (
                <span>
                  {result.createdKeys} of {result.newKeysCount} localization
                  keys have been added to the system.
                  {hasFailedKeys ? (
                    <span className="block mt-2 text-admin_warning">
                      {result.failedKeysCount} keys could not be processed.
                    </span>
                  ) : null}
                </span>
              ) : scanResults && scanResults.newKeysCount > 0 ? (
                <span>
                  {scanResults.newKeysCount} new localization keys have been
                  added to the system.
                </span>
              ) : (
                <span>
                  All localization keys have been updated in the system.
                </span>
              )}
            </Typography>

            <div className="flex flex-col sm:flex-row justify-center gap-4">
              {hasFailedKeys && failedKeys.length > 0 ? (
                <Button
                  variant="admin_secondary"
                  className="w-full sm:w-auto"
                  onClick={openFailedKeysModal}
                >
                  See Failed Keys
                </Button>
              ) : null}
              <Button
                variant="admin_primary"
                className="w-full sm:w-32"
                onClick={onClose}
              >
                Close
              </Button>
            </div>
          </div>
        )

      case "error":
        return (
          <div className="flex flex-col p-8">
            <Typography
              variant="h5"
              className="text-admin_error mb-4 text-center"
            >
              Error
            </Typography>
            <Typography variant="admin_body" className="mb-6 text-center">
              {error || "An unknown error occurred"}
            </Typography>
            <div className="flex justify-center">
              <Button
                variant="admin_primary"
                className="w-full sm:w-32"
                onClick={startScan}
              >
                Try Again
              </Button>
            </div>
          </div>
        )

      default:
        return null
    }
  }

  return (
    <>
      <Modal
        variant="admin"
        isOpen={isOpen}
        onClose={onClose}
        className="relative sm:w-[500px] sm:max-w-[600px] h-full md:h-auto"
        title="Rebuild Localization Database"
      >
        {renderDialogContent()}
      </Modal>

      {/* Failed Keys Modal */}
      <FailedKeysModal
        isOpen={isFailedKeysModalOpen}
        onClose={closeFailedKeysModal}
        failedKeys={failedKeys}
      />
    </>
  )
}

export default LocalizationRebuildDialog
